import { sponsorTypeToWire } from '@/scheduling/lib/sponsor-type-wire';
import { cssColorToFlutter } from '@/shared/lib/flutter-color';
import type { AppointmentFormValues } from '@/types/appointment-form';
import type {
  AppointmentResponse,
  CreateAppointmentRequest,
  FlagResponse,
} from '@/types/scheduling-api';

/**
 * Build the EDIT (PUT /api/Appointment/{id}) body — Flutter parity:
 * `UpdateAppointmentEvent(selectedAppointment)`, which mutates the loaded
 * appointment in place and sends the WHOLE object back.
 *
 * Verified against the dev curl 2026-06-02 (see CONVERSION_NOTES.md):
 * the update body is the loaded appointment ECHOED (top-level
 * `patientId`/`practitionerId` null, identity in `participant[]`, plus
 * `referenceId`/`created`/`practice`/`room`/full `invoice`/full
 * `appointmentProfile.billingProfile`). So we do NOT run
 * `transformFormToRequest` (that builds the Book shape); instead the
 * caller spreads the loaded payload and overlays ONLY the editable
 * fields this function returns:
 *
 *     const body = { ...(loadedAppt as unknown as CreateAppointmentRequest),
 *                    ...formEditOverlay(form, loadedAppt, userId) };
 *
 * Same overlay pattern `appointment-details-panel.tsx:268` already uses
 * for the note/RFV save — extended here to every editable section.
 *
 * Editability (Flutter gates — see CONVERSION_NOTES.md):
 *   - Provider: never editable → NOT overlaid (loaded participant kept).
 *   - Payment (sponsorType/sponsor/policyNo): always editable → overlaid.
 *   - Everything else: status-gated upstream (the sections are read-only
 *     when status ∉ {Booked,Proposed,Pending}), so if the form value
 *     equals the loaded value the overlay is a no-op anyway.
 */

type EditOverlay = Partial<CreateAppointmentRequest> & {
  reminderProfile?: AppointmentResponse['reminderProfile'];
};

/**
 * Server-assigned / computed fields that PreLoad echoes but that must NOT
 * be written back on the PUT. The create POST omits all of these and
 * succeeds, so they're optional on the command — stripping them keeps a
 * future read-only/audit field the API might add (version stamps, etc.)
 * from corrupting the echo PUT (Samer review on PR #408 §2). Verified the
 * PUT still returns 200 with these removed (2026-06-02).
 */
const READ_ONLY_ECHO_KEYS = [
  'created',
  'createdPerson',
  'updatedPerson',
  'referenceId',
] as const;

/**
 * Rebuild the invoice to Flutter's full `Invoice.toJson()` shape. React's
 * loaded `AppointmentInvoiceModel` is sparse, so the PUT was emitting a
 * partial invoice; Flutter always ships the complete key set (mostly
 * null/0). Real values are sourced from the loaded invoice; the rest default
 * to Flutter's constants. Keeps property presence + null/`[]` types in parity
 * so a divergent field can't surprise the backend.
 */
function toFlutterInvoiceShape(inv: unknown): Record<string, unknown> {
  const i = (inv ?? {}) as Record<string, unknown>;
  return {
    organization: null,
    id: i.id ?? null,
    providerName: null,
    resubmissionAttachmentFileUrl: null,
    resubmissionAttachmentFileName: null,
    patientId: null,
    providerId: null,
    practiceId: null,
    resubmissionComment: null,
    invoiceNumber: i.invoiceNumber ?? '',
    invoiceStatus: i.invoiceStatus ?? '',
    policyNo: i.policyNo ?? '',
    sponsorType: null,
    sponsor: i.sponsor ?? null,
    provider: null,
    appointmentId: i.appointmentId ?? null,
    trnNumber: null,
    encounterId: null,
    locationId: null,
    encounterNumber: null,
    managementDiscount: null,
    invoiceDate: i.invoiceDate ?? null,
    draftedOn: i.draftedOn ?? null,
    encounterDate: null,
    patient: null,
    practice: null,
    claim: null,
    diagnosisList: null,
    services: Array.isArray(i.services) ? i.services : [],
    note: null,
    grossTotal: i.grossTotal ?? 0,
    discountTotal: i.discountTotal ?? 0,
    netTotal: i.netTotal ?? 0,
    patientPayable: i.patientPayable ?? 0,
    deposited: i.deposited ?? 0,
    sponsorOutstanding: i.sponsorOutstanding ?? 0,
    sponsorPaymentTotal: i.sponsorPaymentTotal ?? 0,
    patientPaymentStatus: i.patientPaymentStatus ?? 'N/A',
    patientOutstanding: i.patientOutstanding ?? 0,
    excludedTotal: i.excludedTotal ?? 0,
    patientShareTotal: i.patientShareTotal ?? 0,
    sponsorAmountTotal: i.sponsorAmountTotal ?? 0,
    eligibilityEndDate: null,
    eligibilityStartDate: null,
    errors: null,
    assessmentPlan: null,
  };
}

/**
 * Build the full EDIT PUT body: the loaded appointment with read-only
 * fields stripped, the editable overlay applied, then normalized to
 * Flutter's `Appointment.toJson()` shape so the PUT body matches the
 * (known-good) Flutter payload field-for-field — same property presence,
 * same `null`/`[]` types. This removes React↔Flutter divergence as a source
 * of backend 500s (e.g. a `null` `policyNo` hitting a protobuf non-null
 * setter, or a `null` list the backend iterates).
 *
 * NOTE: the complex nested objects (`participant`, `appointmentProfile`,
 * `reminderProfile`) are intentionally carried verbatim from the loaded
 * echo — they already round-trip; reshaping them has 500'd the PUT before.
 */
export function buildEditAppointmentBody(
  form: AppointmentFormValues,
  loadedAppt: AppointmentResponse,
  userId: string,
): CreateAppointmentRequest {
  const base: Record<string, unknown> = {
    ...(loadedAppt as unknown as Record<string, unknown>),
  };
  for (const key of READ_ONLY_ECHO_KEYS) delete base[key];

  const merged: Record<string, unknown> = {
    ...base,
    ...formEditOverlay(form, loadedAppt, userId),
  };
  const la = loadedAppt as unknown as Record<string, unknown>;

  // Flutter-parity normalization of the FLAT fields. Nullable props Flutter
  // emits as explicit `null` (never omitted); list props as `[]` (never
  // null); `policyNo` never null; the invoice as the full shape. React-only
  // extras Flutter doesn't send are dropped.
  const parity: Record<string, unknown> = {
    serviceCategoryId: la.serviceCategoryId ?? null,
    serviceTypeId: la.serviceTypeId ?? null,
    recurringId: la.recurringId ?? null,
    patientId: la.patientId ?? null,
    practitionerId: la.practitionerId ?? null,
    createdBy: la.createdBy ?? null,
    questionnaireFormId: la.questionnaireFormId ?? null,
    isCreatedByPatient: la.isCreatedByPatient ?? null,
    referenceAppointmentId: la.referenceAppointmentId ?? null,
    followUpId: la.followUpId ?? null,
    isRelatedPerson: la.isRelatedPerson ?? null,
    questionnaire: la.questionnaire ?? null,
    editRecurring: la.editRecurring ?? null,
    appointmentStatus: la.appointmentStatus ?? null,
    residentialAddressId:
      la.residentialAddressId ??
      (la.residentialAddress as { id?: string } | undefined)?.id ??
      null,
    callBackContractId: la.callBackContractId ?? '',
    // policyNo — never null (self-pay was sending null → protobuf 500).
    policyNo:
      (merged.policyNo as string | null | undefined) ??
      (la.policyNo as string | null | undefined) ??
      ((la.invoice as { policyNo?: string } | undefined)?.policyNo ?? ''),
    // List props — `[]` not null (a null list the backend iterates → NRE).
    addedServices: Array.isArray(la.addedServices) ? la.addedServices : [],
    addedIndividualServices: Array.isArray(la.addedIndividualServices)
      ? la.addedIndividualServices
      : [],
    invoice: toFlutterInvoiceShape(merged.invoice ?? la.invoice),
  };

  const body: Record<string, unknown> = { ...merged, ...parity };
  // React-only extras Flutter never sends (backend ignores them; drop for parity).
  delete body.slot;
  delete body.billingTotal;
  delete body.callBackContract;

  return body as unknown as CreateAppointmentRequest;
}

export function formEditOverlay(
  form: AppointmentFormValues,
  loadedAppt: AppointmentResponse,
  userId: string,
): EditOverlay {
  // Sponsor casing — form enum "Self-Pay" → wire "Self-pay" (lowercase p).
  // Centralised in sponsor-type-wire.ts (round-trip tested).
  const sponsorTypeWire = sponsorTypeToWire(form.sponsorType);
  const isSelfPay = sponsorTypeWire === 'Self-pay';

  // reasonCode — dev ships lowercase UUID code + system:null
  // (transform-form-to-request.ts:77). Preserve the loaded system when
  // the form's reasonCode carries none.
  const reasonCode = form.reasonCode
    ? {
        code: (form.reasonCode.code ?? '').toLowerCase(),
        display: form.reasonCode.display ?? '',
        definition: form.reasonCode.definition ?? '',
        system: loadedAppt.reasonCode?.system ?? null,
      }
    : loadedAppt.reasonCode;

  const appointmentType = form.appointmentType
    ? {
        code: form.appointmentType.code,
        display: form.appointmentType.display,
        definition: form.appointmentType.definition ?? null,
        system: null,
      }
    : loadedAppt.appointmentType;

  // Nested objects are kept from the LOADED payload unless the user
  // actually changed the selection. The create-panel sections re-hydrate
  // these into create/display-optimised shapes (e.g. a profile fetched
  // without its full billingProfile), so blindly sending the form's copy
  // 500s the PUT even on an unchanged save. Comparing the id lets a REAL
  // change (picker → new full object) through while keeping the
  // wire-perfect loaded object for the no-change case.
  const loadedProfileId =
    (loadedAppt.appointmentProfile as { referenceId?: string } | undefined)
      ?.referenceId ?? loadedAppt.appointmentProfile?.id;
  const profileChanged =
    !!form.appointmentProfileId && form.appointmentProfileId !== loadedProfileId;
  const appointmentProfile = profileChanged
    ? (form.pickedProfile as CreateAppointmentRequest['appointmentProfile'])
    : (loadedAppt.appointmentProfile as CreateAppointmentRequest['appointmentProfile']);

  const reminderChanged =
    !!form.reminderProfileId &&
    form.reminderProfileId !== loadedAppt.reminderProfile?.id;
  const reminderProfile = reminderChanged
    ? (form.reminderProfile as AppointmentResponse['reminderProfile'])
    : loadedAppt.reminderProfile;

  // Flag list — `{ code:number, display, categoryName:null }`
  // (transform-form-to-request.ts:107). Auto-hydration can repopulate
  // `patientFlags` with the patient's full flag set (not the appointment's
  // chosen subset), so keep the loaded list verbatim for now. (Flag
  // editing in edit mode → follow-up via RHF dirtyFields.)
  const flagList: FlagResponse[] | undefined = loadedAppt.flagList;

  // MINIMAL overlay. Verified 2026-06-02: echoing the loaded PreLoad
  // appointment back via PUT returns 200, so the base `{...loadedAppt}`
  // the caller spreads is already wire-valid. We therefore overlay ONLY
  // the genuinely-editable fields and let the spread carry everything else
  // verbatim (location, appointmentType, roomId, minutesDuration,
  // priority, description, updatedBy, residentialAddressId,
  // callBackContractId, flagList…). Overriding those pass-throughs with
  // form-derived/reshaped copies was 500ing an otherwise-unchanged save.
  const overlay: EditOverlay = {
    // Flutter sets `selectedAppointment.updatedBy = userData.userId` on
    // every edit (view_appointment_screen.dart:976) — a safe scalar.
    updatedBy: userId,
    // Note (a safe overlay field — the same { ...loadedAppt, comment }
    // pattern the prior note/RFV save used).
    comment: form.comment ?? null,
    // RFV.
    reasonCode,
    // Reschedule — strings, no-Z (loaded + slot picker are already no-Z).
    start: form.slotStart ?? loadedAppt.start,
    end: form.slotEnd ?? loadedAppt.end,
    // Payment — always editable (no status gate).
    sponsorType: sponsorTypeWire,
    policyNo: form.policyNo ?? null,
    // Sponsor — Flutter parity. Self-pay MUST send null (sending the stale
    // loaded insurance sponsor with sponsorType "Self-pay" 500s the PUT).
    // Insurance/Corporate send the FULL payer incl. payerCompanyId + relatedTpa
    // (a sparse sponsor stores a null payerCompanyId → eRx→DHPO empty
    // ReceiverID/PayerID). When the picker didn't change the selection,
    // loadedAppt.sponsor already carries the full payer from PreLoad.
    sponsor: isSelfPay
      ? null
      : form.sponsor?.id
        ? {
            id: form.sponsor.id,
            name: form.sponsor.payerName ?? form.sponsor.name ?? '',
            payerName: form.sponsor.payerName,
            imageUrl: form.sponsor.imageUrl,
            payerCompanyId: form.sponsor.payerCompanyId,
            relatedTpa: form.sponsor.relatedTpa,
          }
        : (loadedAppt.sponsor ?? null),
    // Forms / questionnaire.
    questionnaireForm: form.questionnaireId
      ? { questionnaireId: form.questionnaireId, name: form.questionnaireName ?? '' }
      : (loadedAppt.questionnaireForm ?? null),
  };

  // Profile change — only when the user actually picked a different
  // profile; carries the dependent fields the new profile implies.
  if (profileChanged) {
    // Normalise the picked profile's colour to Flutter ARGB — a CSS
    // `#RRGGBB` here crashes the Flutter calendar card (int.tryParse →
    // null → throw → gray box). Keep the original if it isn't recognised.
    overlay.appointmentProfile = appointmentProfile
      ? {
          ...appointmentProfile,
          color: cssColorToFlutter(appointmentProfile.color) ?? appointmentProfile.color,
        }
      : appointmentProfile;
    overlay.appointmentType = appointmentType;
    overlay.minutesDuration = form.minutesDuration ?? loadedAppt.minutesDuration;
    overlay.roomId =
      (form.pickedProfile as { roomId?: string } | null)?.roomId ?? loadedAppt.roomId;
    if (form.location) {
      overlay.location = {
        ...loadedAppt.location,
        id: form.location.id,
        name: form.location.name,
      };
    }
  }

  // Reminder change — only when the picker changed the selection.
  if (reminderChanged) {
    overlay.reminderProfile = reminderProfile;
  }

  // `flagList` intentionally NOT overlaid (kept from the base spread) —
  // see the note above; flag editing in edit mode is a follow-up.
  void flagList;

  return overlay;
}
